import React, { Component, RefObject } from 'react';

import get from 'lodash/get';

import Banner from '@atlaskit/banner';
import ErrorIcon from '@atlaskit/icon/glyph/error';

import {
  NavigateButtons,
  TestCaseBoxStepsActions,
  TestCaseBoxTitle,
  TestSteps,
} from '@qality/common-components';
import Loading from '@qality/common-components/src/atoms/Loading/Loading';
import { PremiumFeatureGuard } from '@qality/common-components/src/atoms/PremiumFeatureGuard';
import { LastExecutionBar } from '@qality/common-components/src/molecules/LastExecutionBar';
import { messages } from '@qality/common-messages';
import {
  DETAIL_VIEW,
  LastExecution,
  TestCase,
  TestStep,
  TestStepsCollection,
  TestStepsView,
} from '@qality/common-model';
import {
  QAlityIssueViewInitProps,
  TestCaseFullScreenCloseCallbackData,
} from '@qality/common-model/src/TestCaseFullScreenData';
import { TestTypeProvider } from '@qality/common-pages/src/TestTypesPage/TestTypesProvider';
import {
  IssueService,
  StorageOfUniqueServices,
  TestExecutionService,
  TestStepService,
  ViewService,
} from '@qality/common-service';
import { QAlityIssueViewContext } from '@qality/common-state';

import IssueTestTypeProvider from './IssueTestTypeProvider';
import './_test-step-issue-view.scss';

type Props = {
  dialogProps?: {
    isInDialog: boolean;
    onDialogClose: () => void;
  };
  initProps?: QAlityIssueViewInitProps;
};

type State = {
  isLoading: boolean;
  isFetchingError: boolean;
  issueKey: string;
  view: TestStepsView;
  testCase: TestCase;
  issueId?: number;
  testSteps?: TestStepsCollection;
  lastExecution: LastExecution;
};

export default class TestStepsIssueView extends Component<Props, State> {
  static defaultProps = {
    dialogProps: {
      isInDialog: false,
    },
  };

  static contextType = QAlityIssueViewContext;

  containerRef: RefObject<HTMLDivElement>;

  constructor(props: Props) {
    super(props);
    this.containerRef = React.createRef();
    StorageOfUniqueServices.get('ResizeService').init(this.containerRef);
    this.state = {
      view: null,
      issueKey: '',
      issueId: undefined,
      isLoading: true,
      testCase: null,
      isFetchingError: false,
      lastExecution: null,
    };
  }

  initFromProps = (): void => {
    const { testCase, lastExecution } = this.props.initProps;
    const { testSteps, issue } = testCase;

    this.setState(
      {
        testCase,
        testSteps,
        view: DETAIL_VIEW,
        // @ts-expect-error <here Issue is of type Issue not promise>
        issueKey: issue.key,
        // @ts-expect-error <here Issue is of type Issue not promise>
        issueId: issue.id,
        isLoading: false,
        lastExecution,
      },
      () => {
        StorageOfUniqueServices.get('ResizeService').resize();
      }
    );
  };

  initDefault = async (): Promise<void> => {
    try {
      const issue = this.context.currentIssue;
      const issueId = issue.id;
      const [testSteps, [lastExecution]] = await Promise.all([
        TestStepService.getTestStepsForIssue(issueId),
        TestExecutionService.getLastTestExecutionData([issueId]),
      ]);
      const testCase = new TestCase(
        issue.id,
        issue.fields.summary,
        testSteps,
        issue,
        true
      );
      this.setState(
        {
          isLoading: false,
          testCase,
          issueKey: issue.key,
          issueId,
          testSteps,
          view: ViewService.fetchView(issue.key),
          lastExecution,
        },
        () => {
          StorageOfUniqueServices.get('ResizeService').resize();
        }
      );
    } catch (e) {
      this.setState({
        isFetchingError: true,
        isLoading: false,
      });
    }
  };

  async componentDidMount(): Promise<void> {
    await StorageOfUniqueServices.get('AttachmentConfigService').init();

    if (this.props.initProps) {
      this.initFromProps();
      return;
    }

    this.initDefault();
  }

  changeView = (value: TestStepsView): void => {
    if (!this.props.dialogProps.isInDialog) {
      ViewService.saveView(this.state.issueKey, value);
    }
    this.setState({ view: value });
  };

  getErrorFetchingBanner(): JSX.Element {
    return (
      <Banner
        icon={<ErrorIcon label='Error icon' secondaryColor='inherit' />}
        appearance='error'
      >
        {messages['error.qalityIssueView.fetching.message']}
      </Banner>
    );
  }

  saveTestCaseName = (name: string): void => {
    const testCase = { ...this.state.testCase, name };
    this.setState({
      testCase,
    });
    if (get(testCase.issue, 'fields.summary') !== testCase.name) {
      IssueService.updateIssue(testCase.issue.key, testCase.name);
    }
  };

  syncDataFromFullScreen = ({
    testCase,
  }: TestCaseFullScreenCloseCallbackData): void => {
    this.setState({
      testCase,
      testSteps: testCase.testSteps,
    });
  };

  getTestCaseId(): number {
    return Number(this.state.issueId);
  }

  getQAlityIssueViewContent(): JSX.Element {
    if (this.showLoading()) {
      return <Loading relative spinnerSize='large' fontSize='large' />;
    }

    return (
      <TestTypeProvider>
        <IssueTestTypeProvider testCaseIds={[this.getTestCaseId()]}>
          <div ref={this.containerRef}>
            {!this.props.dialogProps.isInDialog && (
              <NavigateButtons
                changeView={this.changeView}
                view={this.state.view}
                canEdit={this.context.canEdit}
              />
            )}
            <TestCaseBoxTitle
              canEdit={this.context.canEdit}
              name={this.state.testCase?.name}
              saveTestCaseName={this.saveTestCaseName}
              addTestStep={this.addTestStep}
              expanded={true}
              isTestStepView={true}
              testCase={this.state.testCase}
              issueKey={this.state.issueKey}
              isFullscreenMode={this.props.dialogProps.isInDialog}
              isIssueView={false}
              viewProps={{
                onViewChange: this.changeView,
                view: this.state.view,
              }}
              syncDataFromFullScreen={this.syncDataFromFullScreen}
              onDialogClose={this.props.dialogProps.onDialogClose}
              lastExecution={this.state.lastExecution}
            />
            <PremiumFeatureGuard
              premium={
                <LastExecutionBar
                  testCase={this.state.testCase}
                  issueKey={this.state.issueKey}
                  lastExecution={this.state.lastExecution}
                />
              }
            />

            <TestSteps
              canEdit={this.context.canEdit}
              expanded={true}
              testStepsCollection={this.state.testSteps}
              view={this.state.view}
              onUpdateTestSteps={this.updateTestSteps}
              testCaseIssueId={this.state.issueId}
              testCaseIssueKey={this.state.issueKey}
              droppableId='test-steps'
              isFullscreenMode={this.props.dialogProps.isInDialog}
            />
            {!!this.state.issueId && (
              <TestCaseBoxStepsActions
                canEdit={this.context.canEdit}
                addTestStep={this.addTestStep}
              />
            )}
          </div>
        </IssueTestTypeProvider>
      </TestTypeProvider>
    );
  }

  addTestStep = async (): Promise<boolean | void> => {
    try {
      const oldTestSteps = this.state.testSteps?.testSteps;
      const testSteps = new TestStepsCollection(
        await Promise.all(
          oldTestSteps.map(async (testStep) => {
            return TestStepService.createNewTestStepIfNeeded(
              testStep,
              this.state.issueId
            );
          })
        )
      );
      const testCaseId = this.state.issueId;
      testSteps.testSteps.push(new TestStep(testCaseId));
      this.setState({ testSteps });
      this.updateTestCase({ ...this.state.testCase, testSteps });
    } catch (e) {
      StorageOfUniqueServices.get('ErrorHandlingUtils').handleError(
        e,
        messages['error.testCaseBox.saveTestStep.message']
      );
      return true;
    }
  };

  updateTestSteps = async (testSteps: TestStepsCollection): Promise<void> => {
    this.setState({ testSteps });
    this.updateTestCase({ ...this.state.testCase, testSteps });
  };

  updateTestCase = (testCase: TestCase): void => {
    this.setState({
      testCase,
    });
  };

  showLoading(): boolean {
    return this.state.isLoading;
  }

  render(): JSX.Element {
    if (this.state.isFetchingError) {
      return this.getErrorFetchingBanner();
    }
    return this.getQAlityIssueViewContent();
  }
}
